(ns app.front.view.profile.api-keys
  (:require ["@carbon/icons-react/es/edit/24" :default Edit24]
            [app.domain.group.model :as group-model]
            [app.front.clipboard :as clipboard]
            [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-api-keys
  (fn [{:keys [db]} _]
    {:comm/command {:command          :user/api-keys
                    :method           :get
                    :on-success       [::fetch-api-keys.success]
                    :finally-dispatch [::fetch-api-keys.done]}
     :db           (assoc-in db [::data ::loading?] true)}))

(rf/reg-event-db ::fetch-api-keys.success
  (fn [db [_ _ api-keys]]
    (assoc-in db [::data ::api-keys] api-keys)))

(rf/reg-event-db ::fetch-api-keys.done
  (fn [db _]
    (assoc-in db [::data ::loading?] false)))

(rf/reg-event-fx ::add-api-key
  (fn [_ _]
    {:comm/command {:command    :user/create-api-key
                    :method     :post
                    :on-success [::add-api-key.success]
                    :notify     {:success :api-key-ui/add-new-success}}}))

(rf/reg-event-db ::add-api-key.success
  (fn [db [_ _ new-key]]
    (update-in db [::data ::api-keys] concat [new-key])))

(rf/reg-event-fx ::update-description
  (fn [_ [_ data success-evt]]
    {:comm/command {:command    :user/update-api-key-description
                    :data       data
                    :on-success success-evt
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-fx ::regenerate-client-secret
  (fn [_ [_ id success-evt]]
    {:comm/command {:command    :user/update-api-key-secret
                    :data       {:db/id id}
                    :on-success success-evt
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-db ::update-api-key
  (fn [db [_ _ {:db/keys [id] :as new-data}]]
    (update-in db
               [::data ::api-keys]
               (fn [api-keys]
                 (p/update-first api-keys {:db/id id} merge new-data)))))

(rf/reg-event-fx ::delete-api-key
  (fn [_ [_ id success-evt]]
    {:comm/command {:command    :user/delete-api-key
                    :data       {:db/id id}
                    :on-success (conj success-evt id)
                    :notify     {:success :api-key-ui/delete-success}}}))

(rf/reg-event-db ::delete-api-key.success
  (fn [db [_ id]]
    (update-in db
               [::data ::api-keys]
               (fn [api-keys]
                 (remove #(-> % :db/id (= id)) api-keys)))))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::loading?
  :<- [::data]
  :-> ::loading?)

(rf/reg-sub ::api-keys
  :<- [::data]
  #(->> %
        ::api-keys
        (sort-by :entity/created)))

(defn api-key-description [tr {:api-key/keys [description] :as api-key} update-success-evt]
  (r/with-let [*editing? (r/atom false)
               *value    (r/atom description)]
    [:div.flex.items-center
     (if @*editing?
       [:div.flex.items-end.-mt-1.w-full
        [ui/text-input {:id        (str "desc-edit-" (:db/id api-key))
                        :value     @*value
                        :on-change #(reset! *value (futil/tval %))}]
        [ui-config-pills/add-cancel-buttons
         (util/mmap {:on-add    #(do (reset! *editing? false)
                                     (>evt [::update-description
                                            {:db/id               (:db/id api-key)
                                             :api-key/description @*value}
                                            update-success-evt]))
                     :on-cancel #(do (reset! *editing? false)
                                     (reset! *value description))
                     :disabled? (= description @*value)}
                    tr)]]
       [:<>
        [:span.mr-1 description]
        (cc/icon-button {:render-icon      Edit24
                         :kind             :ghost
                         :size             :small
                         :icon-description (tr [:api-key-ui/edit-description])
                         :on-click         #(reset! *editing? true)})])]))

(defn api-key-table [{:keys [tr api-keys update-success-evt delete-success-evt]}]
  [ui-table/modern-responsive-table
   {:class        "w-full"
    :cols         [[:api-key/description
                    (fn [k]
                      [api-key-description tr k update-success-evt])]
                   [:api-key/client-id
                    (fn [{:api-key/keys [client-id]}]
                      [:div.flex.items-center
                       [:span.mr-1.font-mono client-id]
                       (cc/copy-button {:on-click #(clipboard/copy-text client-id)})])
                    :api-key/client-id]
                   [:api-key/client-secret
                    (fn [{:api-key/keys [client-secret]}]
                      (if client-secret
                        [:div
                         [:div.flex.items-center
                          [:span.mr-1.font-mono client-secret]
                          (cc/copy-button {:on-click #(clipboard/copy-text client-secret)})]
                         [:div.text-xs.font-bold (tr [:api-key-ui/display-once-warning])]]
                        (str "<" (tr [:api-key-ui/secret-hidden]) ">")))]]
    :make-actions (fn [{:db/keys [id]}]
                    [{:title           (tr [:api-key-ui/regenerate-secret])
                      :on-click        #(>evt [::regenerate-client-secret id update-success-evt])
                      :confirm?        true
                      :confirm-content (tr [:api-key-ui/regenerate-secret-confirm])}
                     {:title           (tr [:api-key-ui/delete-key])
                      :on-click        #(>evt [::delete-api-key id delete-success-evt])
                      :confirm?        true
                      :confirm-content (tr [:api-key-ui/delete-key-confirm])}])
    :items        api-keys
    :tr           tr}])

(defn section []
  (let [tr       (<sub [:tr])
        loading? (<sub [::loading?])
        api-keys (<sub [::api-keys])
        groups   (<sub [:session/authority-groups])]
    (when (or (seq api-keys)
              (some group-model/use-api-keys? groups))
      [:section
       [:h3 (tr [:api-key-ui/section-title])]
       (if loading?
         [:div
          (cc/inline-loading {:description (tr [:loading])})]
         [:div.mt-2
          [api-key-table (util/mmap {:update-success-evt [::update-api-key]
                                     :delete-success-evt [::delete-api-key.success]}
                                    tr api-keys)]
          [:div.mt-6.flex.w-full.justify-end
           (cc/button {:icon     i/plus
                       :on-click #(>evt [::add-api-key])
                       :size     :field}
                      (tr [:api-key-ui/add-new]))]])])))
